import React, { useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faSortUp, faSortDown, faSort, faList } from '@fortawesome/free-solid-svg-icons'
import { ResponsiveTable, TableEmptyState } from '../TableComponents'
import { StatusChip } from '../atoms'
import { formatDate } from '../../services/format'
import styles from './QASummaryTable.module.scss'

const criteriaLabels = {
  callerNameConfirmation: 'Caller Name',
  companyNameConfirmation: 'Company Name',
  emailPhonetics: 'Email Verification',
  phoneNumberVerification: 'Phone Number',
  opposingPartyPhonetics: 'Opposing Party',
  callNotes5Ws: 'Call Notes (5Ws)'
}

const QASummaryTable = ({
  calls = [],
  loading = false,
  page = 1,
  totalPages = 1,
  total = 0,
  limit = 25,
  sortField = 'qa_evaluation_generated_at',
  sortDirection = 'DESC',
  onSort,
  onPageChange,
  onLimitChange,
  onRowClick
}) => {
  const tableTopRef = useRef(null)

  const renderSortIcon = useCallback((columnId) => {
    const isActive = sortField === columnId
    const icon = isActive
      ? (sortDirection === 'ASC' ? faSortUp : faSortDown)
      : faSort

    return (
      <FontAwesomeIcon
        icon={icon}
        className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}
      />
    )
  }, [sortField, sortDirection])

  const formatFailedCriteria = useCallback((call) => {
    if (call.failedCriteria && Array.isArray(call.failedCriteria)) {
      if (call.failedCriteria.length === 0) return '—'
      return call.failedCriteria.map(key => criteriaLabels[key] || key).join(', ')
    }
    return '—'
  }, [])

  const columns = useMemo(() => [
    {
      id: 'details',
      label: '',
      align: 'center',
      cellClassName: styles.customTableCell,
      style: { width: 60 },
      renderCell: (row) => (
        <Tooltip title="View call details">
          <IconButton
            className={styles.detailsButton}
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onRowClick(row)
            }}
          >
            <FontAwesomeIcon icon={faEye} size="sm" />
          </IconButton>
        </Tooltip>
      )
    },
    {
      id: 'evaluatedAt',
      label: 'QA Date',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 140 },
      renderSortIcon: () => renderSortIcon('qa_evaluation_generated_at'),
      renderCell: (row) => (
        row.evaluatedAt
          ? formatDate(new Date(row.evaluatedAt), 'dateTime')
          : '—'
      )
    },
    {
      id: 'callId',
      label: 'Call ID',
      sortable: false,
      cellClassName: styles.customTableCell,
      style: { minWidth: 100 },
      renderCell: (row) => row.callId || '—'
    },
    {
      id: 'campaignName',
      label: 'Campaign',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 150 },
      renderSortIcon: () => renderSortIcon('campaign_name'),
      renderCell: (row) => row.campaignName || '—'
    },
    {
      id: 'agentName',
      label: 'Agent',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 120 },
      renderSortIcon: () => renderSortIcon('agent_name'),
      renderCell: (row) => row.agentName || '—'
    },
    {
      id: 'disposition',
      label: 'Disposition',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 100 },
      renderSortIcon: () => renderSortIcon('disposition'),
      renderCell: (row) => row.disposition || '—'
    },
    {
      id: 'qaGrade',
      label: 'Grade',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 80 },
      align: 'center',
      renderSortIcon: () => renderSortIcon('qa_grade'),
      renderCell: (row) => (
        <StatusChip
          label={row.qaGrade || 'TBD'}
          size="small"
          color={row.qaGrade === 'PASS' ? 'success' : row.qaGrade === 'FAIL' ? 'error' : 'neutral'}
          variant="soft"
        />
      )
    },
    {
      id: 'overallScore',
      label: 'Score',
      sortable: true,
      cellClassName: styles.customTableCell,
      style: { minWidth: 70 },
      align: 'center',
      renderSortIcon: () => renderSortIcon('overall_score'),
      renderCell: (row) => (
        <span className={styles.scoreText}>
          {row.overallScore != null && row.totalApplicable != null
            ? `${row.overallScore}/${row.totalApplicable}`
            : '—'}
        </span>
      )
    },
    {
      id: 'failed_criteria',
      label: 'Failed Criteria',
      sortable: false,
      cellClassName: styles.customTableCell,
      style: { minWidth: 180 },
      renderCell: (row) => {
        const failedText = formatFailedCriteria(row)
        return (
          <Tooltip title={failedText !== '—' ? failedText : ''}>
            <span className={styles.failedCriteria}>{failedText}</span>
          </Tooltip>
        )
      }
    }
  ], [renderSortIcon, formatFailedCriteria, onRowClick])

  const renderHeaderContent = useCallback(() => (
    <Box className={styles.resultsHeader}>
      <Typography className={styles.resultsTitle}>
        <FontAwesomeIcon icon={faList} className={styles.headerIcon} />
        QA Results
        {total > 0 && (
          <span className={styles.resultCount}>({total.toLocaleString()} calls)</span>
        )}
      </Typography>
    </Box>
  ), [total])

  const handlePageChange = useCallback((_event, value) => {
    if (tableTopRef.current) {
      tableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    onPageChange(value)
  }, [onPageChange])

  const handleLimitChange = useCallback((value) => {
    onLimitChange(value)
  }, [onLimitChange])

  const emptyState = useMemo(() => (
    <TableEmptyState
      searched
      title="No QA Results Found"
      message="No calls match your current filter criteria"
      icon="clipboard-list"
    />
  ), [])

  return (
    <Box className={styles.root}>
      {/* Scroll anchor */}
      <div ref={tableTopRef} style={{ position: 'relative', top: '-100px' }} />

      <ResponsiveTable
        columns={columns}
        data={calls}
        loading={loading}
        searched
        sortField={sortField}
        sortDirection={sortDirection.toLowerCase()}
        onSort={onSort}
        onRowClick={onRowClick}
        variant='qa'
        colSpan={columns.length}
        emptyState={emptyState}
        renderHeaderContent={renderHeaderContent}
        containerProps={{
          className: styles.tableContainer
        }}
        tableProps={{
          'aria-label': 'QA summary results table'
        }}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        limit={limit}
        onLimitChange={handleLimitChange}
        limitOptions={[25, 50, 100]}
        rowHeaderColumn={3}
      />
    </Box>
  )
}

QASummaryTable.propTypes = {
  calls: PropTypes.array,
  loading: PropTypes.bool,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  total: PropTypes.number,
  limit: PropTypes.number,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onLimitChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired
}

export default React.memo(QASummaryTable)
